// src/context/NotificationContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { api } from '../services/api';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

// Helper to suppress route-not-yet-implemented errors quietly
const shouldSuppressError = (errMessage) => {
  if (!errMessage) return false;
  const msg = errMessage.toString().toLowerCase();
  return (
    msg.includes('404') ||
    msg.includes('not registered') ||
    msg.includes('<!doctype html>') ||
    msg.includes('cannot get') ||
    msg.includes('cannot post')
  );
};

export const NotificationProvider = ({ children, userId }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadNotifications = useCallback(async () => {
    if (!userId) return;
    const token = localStorage.getItem('authToken');
    if (!token) return; // silently skip if not logged in
    try {
      setLoading(true);
      setError(null);
      const response = await api.fetchNotifications();

      // Backend: { success, message, data: [...], timestamp }
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];

      // Normalize each notification into a stable shape for the UI
      const normalized = data.map((n) => ({
        id: n.id,
        type: n.type || 'GENERIC',
        title: n.title || 'Notification',
        message: n.message || '',
        isRead: Boolean(n.isRead ?? n.read),
        createdAt: n.createdAt || new Date().toISOString(),
        time: n.time || formatRelative(n.createdAt),
      }));

      setNotifications(normalized);
      setUnreadCount(normalized.filter((n) => !n.isRead).length);
    } catch (err) {
      if (!shouldSuppressError(err.message)) {
        console.error('Failed to fetch notifications:', err.message);
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000); // poll every 30s
    return () => clearInterval(interval);
  }, [userId, loadNotifications]);

  const markRead = async (id) => {
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true, read: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      await api.markAsRead(id);
    } catch (err) {
      if (!shouldSuppressError(err.message)) {
        console.error('Failed to mark as read:', err.message);
      }
      // Re-sync from server on failure
      loadNotifications();
    }
  };

  const markAllRead = async () => {
    const previous = notifications;
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, isRead: true, read: true }))
    );
    setUnreadCount(0);

    try {
      await api.markAllAsRead();
    } catch (err) {
      if (!shouldSuppressError(err.message)) {
        console.error('Failed to mark all as read:', err.message);
      }
      // Roll back
      setNotifications(previous);
      setUnreadCount(previous.filter((n) => !n.isRead).length);
    }
  };

  const addLocalNotification = (message, type = 'GENERIC') => {
    const newNotif = {
      id: `local-${Date.now()}`,
      type,
      title: 'Notification',
      message,
      isRead: false,
      read: false,
      createdAt: new Date().toISOString(),
      time: 'Just now',
    };
    setNotifications((prev) => [newNotif, ...prev]);
    setUnreadCount((prev) => prev + 1);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        error,
        markRead,
        markAllRead,
        addLocalNotification,
        refresh: loadNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// ─── Helpers ───
function formatRelative(iso) {
  if (!iso) return 'Just now';
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}